import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import {
  MapPin, Flag, Truck, Plus, Check, ArrowRight, ArrowLeft, Route, Clock,
  Loader2, CheckCircle2, Sparkles, Repeat, CreditCard, Phone, Navigation, ShieldCheck, Lock,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { AddressSearch } from "@/components/AddressSearch";
import { MapView } from "@/components/MapView";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { PhoneInput } from "@/components/PhoneInput";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

function money(a, c = "EUR") {
  return new Intl.NumberFormat("it-IT", { style: "currency", currency: c, maximumFractionDigits: 0 }).format(Number(a || 0));
}
function duration(min) {
  if (!min) return "—";
  const h = Math.floor(min / 60), m = Math.round(min % 60);
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

const STEP_LABELS = ["wiz.s1", "wiz.s2", "wiz.s3", "wiz.s4", "wiz.s5"];

export function QuoteWizard({ settings, campaign = "", initialPickup = null, initialDestination = null, seoSlug = "" }) {
  const { t, locale } = useI18n();
  const [step, setStep] = useState(initialPickup && initialDestination ? 2 : 0);
  const [pickup, setPickup] = useState(initialPickup);
  const [destination, setDestination] = useState(initialDestination);
  const [transport, setTransport] = useState("");
  const [services, setServices] = useState([]);
  const [estimate, setEstimate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showContact, setShowContact] = useState(false);
  const [contact, setContact] = useState({ name: "", email: "", phone: "+39", deceased_name: "", notes: "", person_type: "individual", company_name: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);
  const [callbackDone, setCallbackDone] = useState(false);
  const [showCallback, setShowCallback] = useState(false);
  const [showIncluded, setShowIncluded] = useState(false);
  const [cbForm, setCbForm] = useState({ preferred_date: "", preferred_time: "", phone: "", note: "" });

  const submitCallback = async () => {
    try {
      await api.post("/public/callback-request", {
        name: contact.name, email: contact.email,
        phone: cbForm.phone || contact.phone,
        preferred_date: cbForm.preferred_date, preferred_time: cbForm.preferred_time,
        note: cbForm.note, quote_id: done?.quote_id || "",
      });
      setCallbackDone(true); setShowCallback(false);
    } catch (e) { toast.error("Errore. Riprova."); }
  };

  const name = (o) => o?.name?.[locale] || o?.name?.en || o?.code;
  const desc = (o) => o?.desc?.[locale] || o?.desc?.en || "";
  const transports = settings?.wizard_transport || [];
  const svcList = settings?.wizard_services || [];
  const currency = settings?.currency || "EUR";

  const toggleService = (code) =>
    setServices((s) => (s.includes(code) ? s.filter((x) => x !== code) : [...s, code]));

  useEffect(() => {
    if (!(pickup?.lat && destination?.lat && transport)) { setEstimate(null); return; }
    const handle = setTimeout(() => {
      setLoading(true);
      api.post("/public/quote-estimate", { pickup, destination, transport_code: transport, service_codes: services })
        .then(({ data }) => setEstimate(data))
        .catch((e) => toast.error(formatApiError(e.response?.data?.detail)))
        .finally(() => setLoading(false));
    }, 350);
    return () => clearTimeout(handle);
  }, [pickup, destination, transport, services]); // eslint-disable-line

  const canNext = () => {
    if (step === 0) return !!pickup?.lat;
    if (step === 1) return !!destination?.lat;
    if (step === 2) return !!transport;
    return true;
  };

  const submit = async () => {
    if (!contact.name || !contact.email) { toast.error("Inserisci nome ed email"); return; }
    setSubmitting(true);
    try {
      const payload = { pickup, destination, transport_code: transport, service_codes: services, ...contact, campaign, seo_slug: seoSlug, locale };
      if (contact.person_type === "company") payload.company_name = contact.name;
      const { data } = await api.post("/public/quote-request", payload);
      // New / passwordless account → auto-logged in; send them to set their password.
      // (The confirmation email with a set-password link is the backup if this redirect fails.)
      if (data.setup_token) {
        const q = new URLSearchParams({ token: data.setup_token, quote: data.quote_id || "", n: data.number || "", t: String(data.estimate?.total ?? ""), c: currency });
        window.location.href = `/set-password?${q.toString()}`;
        return;
      }
      setDone(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSubmitting(false); }
  };

  const payNow = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post(`/public/quote/${done.quote_id}/checkout`, { origin_url: window.location.origin });
      if (data.url) window.location.href = data.url;
      else if (data.already_paid) toast.success(t("order.paid"));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setSubmitting(false); }
  };

  if (done) {
    return (
      <div className="bg-white rounded-2xl shadow-2xl border border-stone-200 p-8 lg:p-10 text-center" data-testid="wizard-success">
        <CheckCircle2 className="mx-auto h-16 w-16 text-emerald-500" />
        <h3 className="font-serif-display text-3xl text-stone-900 mt-4">{t("wiz.thanks")}</h3>
        <p className="text-stone-500 mt-2">{t("wiz.thanksSub")}</p>
        <div className="mt-6 inline-flex flex-col items-center bg-stone-50 rounded-xl px-8 py-5 border border-stone-200">
          <span className="text-xs uppercase tracking-wider text-stone-400">{t("wiz.ref")}</span>
          <span className="font-serif-display text-2xl text-stone-900">{done.number}</span>
          <span className="text-lg font-semibold text-blue-900 mt-2">{money(done.estimate.total, currency)}</span>
        </div>
        {callbackDone ? (
          <p className="text-sm text-emerald-600 mt-6 font-medium" data-testid="wiz-callback-done">{t("wiz.callbackDone")}</p>
        ) : showCallback ? (
          <div className="mt-6 max-w-md mx-auto text-left space-y-3 bg-stone-50 border border-stone-200 rounded-xl p-5" data-testid="wiz-callback-form">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Data preferita</Label><Input type="date" value={cbForm.preferred_date} onChange={(e) => setCbForm({ ...cbForm, preferred_date: e.target.value })} data-testid="cb-date" /></div>
              <div className="space-y-1.5"><Label>Ora preferita</Label><Input type="time" value={cbForm.preferred_time} onChange={(e) => setCbForm({ ...cbForm, preferred_time: e.target.value })} data-testid="cb-time" /></div>
            </div>
            <div className="space-y-1.5"><Label>Telefono</Label><Input value={cbForm.phone} onChange={(e) => setCbForm({ ...cbForm, phone: e.target.value })} placeholder={contact.phone} data-testid="cb-phone" /></div>
            <div className="space-y-1.5"><Label>Nota (opzionale)</Label><Input value={cbForm.note} onChange={(e) => setCbForm({ ...cbForm, note: e.target.value })} data-testid="cb-note" /></div>
            <Button onClick={submitCallback} className="w-full bg-blue-900 hover:bg-blue-800" data-testid="cb-submit">Invia richiesta</Button>
          </div>
        ) : (
          <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
            <Button onClick={payNow} disabled={submitting} className="bg-emerald-600 hover:bg-emerald-700 h-12 px-6" data-testid="wiz-pay-now">
              <CreditCard size={17} className="mr-2" /> {t("wiz.payNow")}
            </Button>
            <Button variant="outline" onClick={() => { setCbForm((f) => ({ ...f, phone: f.phone || contact.phone })); setShowCallback(true); }} className="h-12 px-6" data-testid="wiz-request-callback">
              <Phone size={17} className="mr-2" /> {t("wiz.requestCallback")}
            </Button>
          </div>
        )}
        <p className="text-sm text-stone-400 mt-6">{t("wiz.contactSoon")}</p>
        <p className="text-xs text-stone-500 mt-3 max-w-md mx-auto bg-stone-50 border border-stone-200 rounded-lg px-4 py-2.5" data-testid="wiz-no-hidden-cost">{t("wiz.noHiddenCost")}</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl shadow-2xl border border-stone-200 overflow-hidden" data-testid="quote-wizard">
      {/* header / stepper */}
      <div className="px-6 lg:px-8 pt-6">
        <div className="flex items-center gap-2 text-blue-900">
          <Sparkles size={16} />
          <span className="text-xs font-semibold uppercase tracking-[0.18em]">{t("wiz.title")}</span>
        </div>
        <div className="flex items-center gap-1.5 mt-4">
          {STEP_LABELS.map((_, i) => (
            <div key={i} className={`h-1.5 flex-1 rounded-full transition-colors ${i <= step ? "bg-blue-900" : "bg-stone-200"}`} />
          ))}
        </div>
        <div className="mt-3 text-sm text-stone-400">{t("wiz.step")} {step + 1}/5 · <span className="text-stone-700 font-medium">{t(STEP_LABELS[step])}</span></div>
      </div>

      <div className="p-6 lg:p-8 min-h-[360px]">
        <AnimatePresence mode="wait">
          <motion.div key={step} initial={{ opacity: 0, x: 16 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -16 }} transition={{ duration: 0.25 }}>
            {step === 0 && (
              <div className="space-y-4">
                <h3 className="font-serif-display text-2xl text-stone-900 flex items-center gap-2"><MapPin className="text-blue-700" size={22} /> {t("wiz.pickupTitle")}</h3>
                <AddressSearch value={pickup} onSelect={setPickup} placeholder={t("wiz.pickupPlaceholder")} testid="wiz-pickup" />
                {pickup && <DetailLine place={pickup} />}
                <MapView pickup={pickup} destination={null} height={240} />
              </div>
            )}
            {step === 1 && (
              <div className="space-y-4">
                <h3 className="font-serif-display text-2xl text-stone-900 flex items-center gap-2"><Flag className="text-[#8a6d2f]" size={22} /> {t("wiz.destTitle")}</h3>
                <AddressSearch value={destination} onSelect={setDestination} placeholder={t("wiz.destPlaceholder")} testid="wiz-dest" accent="amber" />
                {destination && <DetailLine place={destination} />}
                <MapView pickup={pickup} destination={destination} height={240} />
              </div>
            )}
            {step === 2 && (
              <div className="space-y-4">
                <h3 className="font-serif-display text-2xl text-stone-900">{t("wiz.transportTitle")}</h3>
                <div className="grid sm:grid-cols-2 gap-3">
                  {transports.map((tr) => {
                    const sel = transport === tr.code;
                    return (
                      <button key={tr.code} type="button" onClick={() => setTransport(tr.code)} data-testid={`wiz-transport-${tr.code}`}
                        className={`text-left p-4 rounded-xl border-2 transition-all ${sel ? "border-blue-900 bg-blue-50/50 shadow-sm" : "border-stone-200 hover:border-stone-300"}`}>
                        <div className="flex items-center justify-between">
                          <Truck size={20} className={sel ? "text-blue-900" : "text-stone-400"} strokeWidth={1.5} />
                          {sel && <Check size={18} className="text-blue-900" />}
                        </div>
                        <div className="font-semibold text-stone-900 mt-2">{name(tr)}</div>
                        <div className="text-xs text-stone-400 mt-0.5">{desc(tr)}</div>
                        <div className="text-sm text-blue-900 font-medium mt-2">{t("wiz.from")} {money(tr.base_price, currency)}</div>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
            {step === 3 && (
              <div className="space-y-4">
                <h3 className="font-serif-display text-2xl text-stone-900">{t("wiz.servicesTitle")}</h3>
                <p className="text-sm text-stone-400">{t("wiz.servicesSub")}</p>
                <div className="grid sm:grid-cols-2 gap-2.5">
                  {svcList.map((sv) => {
                    const mandatory = !!sv.mandatory;
                    const sel = mandatory || services.includes(sv.code);
                    return (
                      <button key={sv.code} type="button" onClick={() => !mandatory && toggleService(sv.code)} disabled={mandatory} data-testid={`wiz-service-${sv.code}`}
                        className={`flex items-center justify-between p-3.5 rounded-xl border transition-all ${sel ? "border-blue-900 bg-blue-50/50" : "border-stone-200 hover:border-stone-300"} ${mandatory ? "cursor-not-allowed" : ""}`}>
                        <div className="flex items-center gap-2.5">
                          <span className={`h-6 w-6 rounded-md flex items-center justify-center ${sel ? "bg-blue-900 text-white" : "bg-stone-100 text-stone-400"}`}>{mandatory ? <Lock size={12} /> : sel ? <Check size={14} /> : <Plus size={14} />}</span>
                          <span className="text-sm font-medium text-stone-800">{name(sv)}</span>
                          {mandatory && <span className="text-[10px] uppercase tracking-wide text-blue-900 bg-blue-100 rounded px-1.5 py-0.5">{t("wiz.mandatory")}</span>}
                        </div>
                        <span className="text-sm text-stone-500">{money(sv.price, currency)}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
            {step === 4 && (
              <div className="space-y-5">
                {!showContact ? (
                  <>
                    <h3 className="font-serif-display text-2xl text-stone-900">{t("wiz.resultTitle")}</h3>
                    {loading && !estimate ? (
                      <CalculatingState pickup={pickup} destination={destination} t={t} />
                    ) : estimate ? (
                      <motion.div className="space-y-4" data-testid="wizard-estimate"
                        initial="hidden" animate="show"
                        variants={{ hidden: {}, show: { transition: { staggerChildren: 0.07 } } }}>
                        <RouteHeader pickup={pickup} destination={destination} eta={duration(estimate.duration_min)} />
                        <motion.div variants={revealV} className="grid grid-cols-2 gap-3">
                          <Metric icon={Route} label={t("wiz.roadDist")}
                            value={`${estimate.road_distance_km ?? estimate.distance_km} km`}
                            sub={(estimate.return_multiplier > 1 && estimate.billable_km)
                              ? `${t("wiz.opDist")}: ${estimate.billable_km} km (${t("wiz.opNote")})`
                              : null} />
                          <Metric icon={Clock} label={t("wiz.eta")} value={duration(estimate.duration_min)} />
                        </motion.div>
                        <motion.div variants={revealV} className="rounded-xl border border-stone-200 p-4 space-y-2.5" data-testid="wiz-included-services">
                          <div className="text-xs uppercase tracking-wider text-stone-400">{t("wiz.included")}</div>
                          <ul className="space-y-1.5">
                            <li className="flex items-center gap-2 text-sm text-stone-700"><Check size={15} className="text-emerald-600 shrink-0" /> {estimate.transport.name}</li>
                            {estimate.services.map((s) => (
                              <li key={s.code} className="flex items-center gap-2 text-sm text-stone-700"><Check size={15} className="text-emerald-600 shrink-0" /> {s.name}</li>
                            ))}
                          </ul>
                          <button type="button" onClick={() => setShowIncluded((v) => !v)} className="text-xs text-blue-800 font-semibold hover:underline" data-testid="wiz-see-included">{t("wiz.seeIncluded")}</button>
                          {showIncluded && (
                            <div className="pt-2 border-t border-stone-100 space-y-2" data-testid="wiz-included-detail">
                              <div className="text-sm"><span className="font-medium text-stone-800">{estimate.transport.name}</span><span className="block text-xs text-stone-500">{t("wiz.inc.transport")}</span></div>
                              {estimate.services.map((s) => (
                                <div key={s.code} className="text-sm"><span className="font-medium text-stone-800">{s.name}</span><span className="block text-xs text-stone-500">{t("wiz.inc.generic")}</span></div>
                              ))}
                            </div>
                          )}
                        </motion.div>
                        <motion.div variants={revealV}
                          className="relative flex items-center justify-between rounded-xl px-5 py-4 overflow-hidden bg-gradient-to-r from-blue-950 via-blue-900 to-stone-900 text-white shadow-lg">
                          {loading && <span className="absolute top-2 right-3"><Loader2 size={14} className="animate-spin text-white/60" /></span>}
                          <div>
                            <span className="text-xs uppercase tracking-wider text-blue-200/80 flex items-center gap-1.5"><ShieldCheck size={13} /> {t("wiz.totalEst")}</span>
                            <span className="block text-[11px] text-blue-200/60 mt-0.5">{estimate.tax > 0 ? t("wiz.vatIncluded") : t("wiz.allIncluded")}</span>
                          </div>
                          <CountUp value={estimate.total} currency={currency}
                            className="font-serif-display text-3xl lg:text-4xl tracking-tight" testid="wizard-total" />
                        </motion.div>
                        <motion.div variants={revealV} className="grid grid-cols-2 gap-2" data-testid="wiz-trust-box">
                          {[t("wiz.trust1"), t("wiz.trust2"), t("wiz.trust3"), t("wiz.trust4")].map((txt, i) => (
                            <div key={i} className="flex items-center gap-1.5 text-xs text-stone-600 bg-stone-50 border border-stone-100 rounded-lg px-2.5 py-2"><Check size={13} className="text-emerald-600 shrink-0" /> {txt}</div>
                          ))}
                        </motion.div>
                        <p className="text-xs text-stone-400 text-center">{t("wiz.disclaimer")}</p>
                      </motion.div>
                    ) : <div className="h-32 flex items-center justify-center text-stone-400">—</div>}
                  </>
                ) : (
                  <div className="space-y-4">
                    <h3 className="font-serif-display text-2xl text-stone-900">{t("wiz.contactTitle")}</h3>
                    <div className="grid sm:grid-cols-2 gap-3">
                      <div className="space-y-1.5 sm:col-span-2"><Label>{t("auth.personType")}</Label>
                        <Select value={contact.person_type} onValueChange={(v) => setContact({ ...contact, person_type: v })}>
                          <SelectTrigger data-testid="wiz-person-type"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="individual">{t("auth.individual")}</SelectItem>
                            <SelectItem value="company">{t("auth.company2")}</SelectItem>
                          </SelectContent>
                        </Select></div>
                      <div className="space-y-1.5"><Label>{contact.person_type === "company" ? t("auth.companyName") : t("wiz.name")} *</Label><Input value={contact.name} onChange={(e) => setContact({ ...contact, name: e.target.value })} data-testid="wiz-name" /></div>
                      <div className="space-y-1.5"><Label>Email *</Label><Input type="email" value={contact.email} onChange={(e) => setContact({ ...contact, email: e.target.value })} data-testid="wiz-email" /></div>
                      <div className="space-y-1.5"><Label>{t("wiz.phone")}</Label><PhoneInput value={contact.phone} onChange={(v) => setContact({ ...contact, phone: v })} testid="wiz-phone" /></div>
                      <div className="space-y-1.5"><Label>{t("wiz.deceased")}</Label><Input value={contact.deceased_name} onChange={(e) => setContact({ ...contact, deceased_name: e.target.value })} data-testid="wiz-deceased" /></div>
                      <div className="space-y-1.5 sm:col-span-2"><Label>{t("wiz.notes")}</Label><Textarea value={contact.notes} onChange={(e) => setContact({ ...contact, notes: e.target.value })} data-testid="wiz-notes" /></div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </motion.div>
        </AnimatePresence>
      </div>

      {/* footer nav */}
      <div className="px-6 lg:px-8 py-5 border-t border-stone-100 bg-stone-50 flex items-center justify-between">
        <Button variant="ghost" disabled={step === 0 && !showContact} onClick={() => (showContact ? setShowContact(false) : setStep((s) => Math.max(0, s - 1)))} data-testid="wiz-back">
          <ArrowLeft size={16} className="mr-1.5" /> {t("common.back")}
        </Button>
        {step < 4 ? (
          <Button onClick={() => setStep((s) => s + 1)} disabled={!canNext()} data-testid="wiz-next" className="bg-blue-900 hover:bg-blue-800 h-11 px-6">
            {t("wiz.continue")} <ArrowRight size={16} className="ml-1.5" />
          </Button>
        ) : !showContact ? (
          <Button onClick={() => setShowContact(true)} disabled={!estimate} data-testid="wiz-continue" className="bg-blue-900 hover:bg-blue-800 h-11 px-6">
            {t("wiz.continue")} <ArrowRight size={16} className="ml-1.5" />
          </Button>
        ) : (
          <Button onClick={submit} disabled={submitting} data-testid="wiz-submit" className="bg-[#8a6d2f] hover:bg-[#75591f] h-11 px-6">
            {submitting ? <Loader2 className="animate-spin" size={16} /> : <>{t("wiz.request")}</>}
          </Button>
        )}
      </div>
    </div>
  );
}

function DetailLine({ place }) {
  return (
    <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-stone-500 px-1">
      {place.city && <span>🏙 {place.city}</span>}
      {place.postcode && <span>✉ {place.postcode}</span>}
      {place.country && <span>🌍 {place.country}</span>}
    </div>
  );
}
function Metric({ icon: Icon, label, value, sub }) {
  return (
    <div className="rounded-xl border border-stone-200 p-3">
      <div className="flex items-center gap-1.5 text-stone-400 text-[10px] uppercase tracking-wider"><Icon size={12} /> {label}</div>
      <div className="font-serif-display text-lg sm:text-xl text-stone-900 mt-1 leading-tight">{value}</div>
      {sub && <div className="text-[11px] text-stone-500 mt-1 leading-tight">{sub}</div>}
    </div>
  );
}
function Row({ label, value, bold, muted }) {
  return (
    <div className={`flex items-center justify-between px-4 py-3 text-sm ${muted ? "text-stone-400" : "text-stone-700"}`}>
      <span className={bold ? "font-semibold text-stone-900" : ""}>{label}</span>
      <span className={bold ? "font-semibold text-stone-900" : ""}>{value}</span>
    </div>
  );
}

const revealV = { hidden: { opacity: 0, y: 12 }, show: { opacity: 1, y: 0 } };

function useCountUp(target, dur = 700) {
  const [val, setVal] = useState(target);
  const fromRef = useRef(target);
  useEffect(() => {
    const from = fromRef.current;
    const start = performance.now();
    let raf;
    const tick = (now) => {
      const p = Math.min(1, (now - start) / dur);
      const eased = 1 - Math.pow(1 - p, 3);
      setVal(from + (target - from) * eased);
      if (p < 1) raf = requestAnimationFrame(tick);
      else fromRef.current = target;
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, dur]);
  return val;
}

function CountUp({ value, currency, className, testid }) {
  const v = useCountUp(Number(value || 0));
  return <span className={className} data-testid={testid}>{money(v, currency)}</span>;
}

function RouteHeader({ pickup, destination, eta }) {
  return (
    <div className="flex items-center gap-3 rounded-xl border border-stone-200 bg-stone-50/70 px-4 py-3" data-testid="wiz-route-header">
      <div className="flex-1 min-w-0">
        <div className="text-[10px] uppercase tracking-wider text-stone-400">Da</div>
        <div className="text-sm font-medium text-stone-900 truncate">{pickup?.city || pickup?.label || "—"}</div>
      </div>
      <div className="relative flex items-center justify-center w-16 shrink-0">
        <div className="absolute inset-x-0 top-1/2 border-t-2 border-dashed border-stone-300" />
        <motion.span
          className="relative bg-blue-900 text-white rounded-full p-1.5 shadow"
          initial={{ x: -22, opacity: 0 }} animate={{ x: 0, opacity: 1 }}
          transition={{ duration: 0.5, ease: "easeOut" }}>
          <Navigation size={13} />
        </motion.span>
      </div>
      <div className="flex-1 min-w-0 text-right">
        <div className="text-[10px] uppercase tracking-wider text-stone-400">A</div>
        <div className="text-sm font-medium text-stone-900 truncate">{destination?.city || destination?.label || "—"}</div>
      </div>
    </div>
  );
}

function CalculatingState({ pickup, destination, t }) {
  return (
    <div className="h-56 flex flex-col items-center justify-center gap-5" data-testid="wiz-calculating">
      <div className="relative h-20 w-20">
        <span className="absolute inset-0 rounded-full border-2 border-blue-100" />
        <motion.span className="absolute inset-0 rounded-full border-2 border-transparent border-t-blue-900"
          animate={{ rotate: 360 }} transition={{ repeat: Infinity, duration: 1, ease: "linear" }} />
        <motion.span className="absolute inset-2 rounded-full border-2 border-transparent border-t-[#8a6d2f]"
          animate={{ rotate: -360 }} transition={{ repeat: Infinity, duration: 1.4, ease: "linear" }} />
        <span className="absolute inset-0 flex items-center justify-center"><Truck size={22} className="text-blue-900" /></span>
      </div>
      <div className="text-center">
        <p className="text-stone-700 font-medium">{t("wiz.calculating")}</p>
        <p className="text-xs text-stone-400 mt-1 truncate max-w-xs">
          {(pickup?.city || "—")} → {(destination?.city || "—")}
        </p>
      </div>
    </div>
  );
}
